/* global AppSettings, AmCharts */
import $ from 'jquery';
import ConfigGenerator from 'components/csrFactoryMap/utils/ConfigGenerator';
import EventDispatcher from 'components/csrFactoryMap/utils/EventDispatcher';
import ResizeManager from 'components/csrFactoryMap/utils/ResizeManager';
import CSRMapDataProvider from 'components/csrFactoryMap/models/CSRMapDataProvider';
import createLogger from 'components/logger/Logger';
import requestIdleCallback from 'components/utils/requestIdleCallback';
import { gsap, Power0, CSSPlugin } from 'gsap/all';

gsap.registerPlugin(Power0, CSSPlugin);

const Logger = createLogger('CSRMapView');

const endpoint = AppSettings.endpoints.supplyChain;

export default class CSRMapView extends EventDispatcher {
  constructor(options) {
    super();
    this.el = options.el;
    this.$el = $(this.el);
    this.collection = options.collection;
    this.initialize();
  }

  initialize() {
    this.cacheSelectors();
    this.initMap();
  }

  // PUBLIC FUNCTIONS
  showAvailableCountries(countries) {
    this.dataProvider.setSelectedCountries(countries);
    this.updateMapSettings();
  }

  updateMapSettings() {
    requestIdleCallback(() => {
      const data = this.dataProvider.getData();
      this.svgMap.dataProvider = data;
      this.svgMap.validateNow();
    });
  }

  setCountry(country) {
    const mapObj = this.svgMap.getObjectById(country.country_code);
    this.svgMap.clickMapObject(mapObj);
  }

  reset() {
    this.selectedMapObject = null;
    this.isResetZoomActive = true;
    this.dataProvider.setSelectedCountry(null);
    this.dataProvider.setDetailSettings(false);
    const data = this.dataProvider.getData();
    this.svgMap.zoomToLongLat(data.zoomLevel, data.zoomLongitude, data.zoomLatitude);
    this.svgMap.validateData();
    gsap.to(this.el, { duration: 0.3, x: 0, ease: Power0.easeNone });
  }

  // END PUBLIC FUNCTIONS
  setupDataProviderModel() {
    this.dataProvider = new CSRMapDataProvider({ data: this.svgMap.dataProvider });
  }

  initMap() {
    // NOTE: Delay is needed in order to bind the init event on time
    const delay = 1;

    new ConfigGenerator({
      endpoint,
    })
      .getMapData()
      .then(mapData => {
        this.svgMap = AmCharts.makeChart(this.el, mapData, delay);
        this.svgMap.addListener('init', e => this.onMapInitialized(e));
      })
      .catch(error => Logger.error(error));
  }

  cacheSelectors() {
    this.ui = {};
  }

  setupEventListeners() {
    this.svgMap.addListener('rollOverMapObject', e => this.onMapObjectOver(e));
    this.svgMap.addListener('rollOutMapObject', e => this.onMapObjectOut(e));
    this.svgMap.addListener('clickMapObject', e => this.onMapObjectClick(e));
    this.svgMap.addListener('zoomCompleted', e => this.onZoomCompleted(e));
    ResizeManager.addListener('resize:change', e => this.onResizeChange(e));
  }

  removeEventListeners() {
    this.$el.off();
  }

  onMapInitialized() {
    this.showGroup('global');
    this.setupDataProviderModel();
    this.setupEventListeners();
    this.resize();

    this.trigger('csr:map:initialized');
  }

  resize() {
    this.mapWidth = this.el.offsetWidth;

    this.vpData = ResizeManager.getViewportData();

    const isMobile = this.vpData.breakpoint === 'xxsmall';
    if (this.isMobile !== isMobile) {
      this.isMobile = isMobile;
      this.dataProvider.setIsMobile(this.isMobile);
    }
    this.sidePannelWidth = this.isMobile ? 0 : 350;

    this.calculateMapPositions();

    this.updateMapSettings();
  }

  onMapObjectOver(e) {
    const type = e.mapObject.objectType;
    if (type === 'MapImage') {
      this.dataProvider.showCountryHoverForImage(e.mapObject);
    } else {
      this.dataProvider.updateLabelOpacityForImage(e.mapObject);
    }
  }

  onMapObjectOut(e) {
    const type = e.mapObject.objectType;
    if (type === 'MapImage') {
      this.dataProvider.showCountryHoverForImage(null);
    } else {
      this.dataProvider.updateLabelOpacityForImage(null);
    }
  }

  onMapObjectClick(e) {
    this.selectedMapObject = this.dataProvider.getMapAreaForObject(e.mapObject);
    this.dataProvider.setSelectedCountry(this.selectedMapObject);
    this.updateMapSettings();
    this.calculateMapPositions();
    this.showGroup('country');

    const countryID = e.mapObject.id || e.mapObject.customData.id;
    const model = this.collection.getModelForCountry(countryID);
    this.trigger('csr:country:select', model);
  }

  calculateMapPositions() {
    if (!this.selectedMapObject) {
      return;
    }
    const long = this.svgMap.getAreaCenterLongitude(this.selectedMapObject);
    const lat = this.svgMap.getAreaCenterLatitude(this.selectedMapObject);
    const countryBB = this.selectedMapObject.displayObject.node.getBBox();
    const scale = this.getScale(this.svgMap.boundingRect, countryBB);
    const coords = this.getCoords(long, lat, this.sidePannelWidth * 0.5, scale);

    this.dataProvider.setDetailSettings(true, scale, coords.longitude, coords.latitude);
    this.svgMap.zoomToLongLat(scale, coords.longitude, coords.latitude);
  }

  getScale(mapBB, countryBB) {
    const margin = 0.9675;
    const scaleW = ((mapBB.width - this.sidePannelWidth) * margin) / countryBB.width;
    const scaleH = (mapBB.height * margin) / countryBB.height;

    // NOTE: 665 is roughly the original size of the SVG,
    // Mobile zooming will be incorrect without this correction.
    const scaleCorrection = (665 / mapBB.height) * 0.9;
    const scale = Math.min(scaleW, scaleH) * scaleCorrection;
    return scale;
  }

  getCoords(long, lat, correction, scale) {
    const pos = this.svgMap.coordinatesToXY(long, lat);
    pos.x -= correction / scale;
    const coords = this.svgMap.xyToCoordinates(pos.x, pos.y);

    // !!NOTE: for some reason this calculation doesn't work for the latitude.
    // so we overwrite it to initial value;
    coords.latitude = lat;
    return coords;
  }

  onZoomCompleted(e) {
    if (this.isResetZoomActive) {
      this.isResetZoomActive = false;
      this.svgMap.validateData();
    }
  }

  onResizeChange(e) {
    this.resize();
  }

  showGroup(group) {
    const groups = this.svgMap.dataProvider.images;
    for (let i = 0; i < groups.length; i++) {
      const { groupId } = groups[i];
      if (groupId === group) {
        this.svgMap.showGroup(groupId);
      } else {
        this.svgMap.hideGroup(groupId);
      }
    }
  }
}
